import type { ClassificationResult } from '@classification-testing/domain';
import { ClassificationType } from '@classification-testing/domain';
import type {
  ClassificationDialogData,
  ClassificationDialogUserInfo,
  ClassificationContentType,
  ClassificationDialogLabel,
  ClassificationDialogPolicy,
} from '@core/domain';
import { toContentType } from '@core/domain/ContentType';

/**
 * Maps ContentType enum to ClassificationContentType string.
 */
function mapContentType(contentType: string): ClassificationContentType {
  return toContentType(contentType) as ClassificationContentType;
}

/**
 * Maps classification labels to the canonical format.
 */
function mapLabels(labels: ClassificationResult['labels']): ClassificationDialogLabel[] {
  return labels.map(label => ({
    name: label.label_name,
    severity: label.severity,
    shortName: label.label_short_name,
    confidence: label.confidence,
    contentType: label.content_type,
    score: label.score,
    labelId: label.label_id,
  }));
}

/**
 * Maps policies to the canonical format.
 */
function mapPolicies(policies: ClassificationResult['policies']): ClassificationDialogPolicy[] | undefined {
  if (!policies || policies.length === 0) return undefined;

  return policies.map(policy => ({
    id: policy.id,
    name: policy.name,
    description: policy.description,
    labelIds: policy.labels?.map(l => l.label_id),
  }));
}

function extractUsername(subjectOwner: NonNullable<ClassificationResult['subjectOwner']>): string {
  const info = subjectOwner.additionalInfo;

  if (typeof info?.username === 'string' && info.username) return info.username;
  if (typeof info?.gamertag === 'string' && info.gamertag) return info.gamertag;

  return subjectOwner.id || 'Unknown User';
}

/**
 * Maps subjectOwner to ClassificationDialogUserInfo.
 * Extracts username from additionalInfo if available, falls back to id.
 */
function mapUserInfo(subjectOwner?: ClassificationResult['subjectOwner']): ClassificationDialogUserInfo | undefined {
  if (!subjectOwner) return undefined;

  return {
    type: subjectOwner.type || 'Unknown',
    userId: subjectOwner.id || 'Unknown',
    username: extractUsername(subjectOwner),
  };
}

/**
 * Converts a ClassificationResult from classification-testing domain
 * to the canonical ClassificationDialogData type used by the core dialog.
 */
export function toClassificationDialogData(result: ClassificationResult): ClassificationDialogData {
  const isSafe = result.classificationType === ClassificationType.SAFE_CONTENT;

  return {
    id: result.id,
    contentType: mapContentType(result.contentType),
    isSafe,
    text: result.text,
    contentUrl: result.contentUrl,
    labels: mapLabels(result.labels || []),
    confidenceScore: result.confidenceScore,
    language: result.languageDetected || result.language,
    timestamp: result.timestamp,
    duration: result.duration,
    explanation: result.explanation,
    triggeredPolicies: mapPolicies(result.policies),
    nonTriggeredPolicies: mapPolicies(result.nonTriggeredPolicies),
    userInfo: mapUserInfo(result.subjectOwner),
    topicName: result.topicName,
    eventType: 'Classification',
    categories: result.categories && result.categories.length > 0 ? result.categories : undefined,
  };
}
